import { NavLink, Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/state/AuthContext.tsx'
import { SECTIONS } from '@/routes/registry.ts'
import { PageBotanicals, LeafDivider } from './Botanical.tsx'
import { initials } from '@/lib/format.ts'
import { useFreshness } from '@/state/useFreshness.ts'

function Monogram({ name, accent }: { name: string; accent: string }) {
  return (
    <span
      className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[0.6rem] font-medium tracking-wider text-paper"
      style={{ background: accent }}
      title={name}
    >
      {initials(name)}
    </span>
  )
}

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const { stale, reload } = useFreshness()

  return (
    <div className="relative min-h-screen">
      <PageBotanicals />

      {stale && (
        <div className="border-b border-linen bg-parchment/70 px-6 py-2.5 text-center text-[0.82rem] text-quiet animate-fadein">
          There's a newer version of this place.{' '}
          <button onClick={reload} className="text-fern underline decoration-sage/40 underline-offset-4 transition-colors hover:decoration-sage">
            Reload to see it
          </button>
        </div>
      )}

      {/* Six sections no longer fit on one line on a phone, so below `sm` the
          title sits above a nav that scrolls sideways rather than wrapping into
          a block of links. */}
      <header className="mx-auto flex max-w-page flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:py-7 sm:px-10">
        <Link to="/" className="group flex items-baseline gap-2.5">
          <span className="font-display text-[1.35rem] leading-none tracking-tight" style={{ fontVariationSettings: "'SOFT' 60, 'WONK' 1" }}>
            Two to Three
          </span>
          <span className="hidden text-[0.62rem] uppercase tracking-[0.25em] text-whisper transition-colors group-hover:text-sage sm:inline">
            est. two
          </span>
        </Link>

        <nav className="-mx-6 flex items-center gap-1 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:gap-2 sm:overflow-visible sm:px-0 sm:pb-0">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.path}
              to={s.path}
              className={({ isActive }) =>
                [
                  'relative shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[0.83rem] transition-colors duration-300 sm:px-4',
                  isActive ? 'text-fern' : 'text-quiet hover:text-fern',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {s.label}
                  <span
                    className={[
                      'absolute inset-x-3 -bottom-0.5 h-px origin-center bg-sage transition-transform duration-400 ease-botanical sm:inset-x-4',
                      isActive ? 'scale-x-100' : 'scale-x-0',
                    ].join(' ')}
                  />
                </>
              )}
            </NavLink>
          ))}

          {user && (
            <button
              onClick={() => void logout()}
              className="ml-2 flex shrink-0 items-center gap-2 rounded-full border border-transparent py-1 pl-1 pr-3 transition-colors hover:border-linen"
              title={`Signed in as ${user.displayName} — sign out`}
            >
              <Monogram name={user.displayName} accent={user.accent} />
              <span className="hidden text-[0.78rem] text-whisper sm:inline">Sign out</span>
            </button>
          )}
        </nav>
      </header>

      <main className="mx-auto max-w-page px-6 pb-28 sm:px-10">{children}</main>

      <footer className="mx-auto flex max-w-page flex-col items-center gap-3 px-6 pb-14">
        <LeafDivider tone="var(--c-sage)" className="h-5 w-44 opacity-45" />
        <p className="text-[0.72rem] tracking-wide text-whisper">Just for the two of us.</p>
      </footer>
    </div>
  )
}

/** Standard page opening: eyebrow, title, and a line of framing. */
export function PageHeading({ eyebrow, title, children }: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <div className="max-w-measure animate-unfurl pb-10 pt-6">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="mt-3 font-display text-[2.6rem] leading-[1.06] sm:text-[3.2rem]">{title}</h1>
      {children && <p className="prose-soft mt-5">{children}</p>}
    </div>
  )
}
